import type { IslandMode, DeckPage } from './IslandProvider';
import type { AnrufKontext } from '../../lib/telefonie/anrufKontext';
import {
  SIZE, SIZE_PEEK, DECK_PAGE_HEIGHT, DECK_PAGE_WIDTH, DECK_HEADER_HEIGHT, SIZE_ANRUF_AUSWAHL_H,
  ANRUF_KONTEXT_ZEILE_H, ANRUF_KONTEXT_TICKET_ZEILE_H, ANRUF_KONTEXT_TRENNER_H, ANRUF_UNBEKANNT_H, IDLE_MIT_TEXT_W, IDLE_TIMER_W,
} from './constants';

/** Unter dieser Breite gilt Handy-Layout (entspricht Tailwind `md`). */
export const MOBIL_BREITE = 768;

export function istMobil(viewportWidth: number): boolean {
  return viewportWidth < MOBIL_BREITE;
}

/** Unter dieser Höhe gilt Handy-Chrome, unabhängig von der Breite: das iPhone
 *  im Querformat misst 844 × 390 — Tablet-Breite, aber kein Platz für
 *  schwebende Panels. iPad quer (1024 × 768) liegt bewusst darüber.
 *  index.html enthält die CSS-Entsprechung `max-height: 519px` (Tailwind-Screen
 *  `kurz`) — beide müssen zusammen geändert werden, siehe Drift-Test in
 *  island-size.test.ts. */
export const KURZ_HOEHE = 520;

export function istKurz(viewportHeight: number): boolean {
  return viewportHeight < KURZ_HOEHE;
}

/** Handy-Verhalten: schmal ODER kurz. */
export function istKompakt(viewportWidth: number, viewportHeight: number): boolean {
  return istMobil(viewportWidth) || istKurz(viewportHeight);
}

interface SizeArgs {
  mode: IslandMode;
  deckPage: DeckPage;
  peeking: boolean;
  controlsContentHeight: number | null;
  formWidth: number | null;
  /** Formular: gemessene Inhaltshöhe (Kopf + Inhalt). Die Pille wächst damit bis
   *  SIZE.form.h; ohne Messung gilt SIZE.form.h. */
  formContentHeight?: number | null;
  /** Anrufkarte: zeigt sie gerade eine Auswahlliste bei mehrdeutigen Nummern?
   *  Nur dann braucht sie die zusätzliche Höhe (SIZE_ANRUF_AUSWAHL_H) statt
   *  der kompakten SIZE.anruf.h. */
  anrufHatAuswahl?: boolean;
  /** Anrufkarte: Objekt/Einheit + offene Vorgänge zum erkannten Anrufer, wenn
   *  vorhanden. Schlägt nur an, solange keine Auswahlliste aussteht (die beiden
   *  sind ohnehin exklusiv — ohne Treffer gibt es keinen Zusammenhang). */
  anrufKontext?: AnrufKontext | null;
  /** Anrufkarte: unbekannte Nummer → Hinweiszeile darunter. */
  anrufUnbekannt?: boolean;
  /** Ruhende Pille zeigt Bereichsname bzw. „N neu“ neben der Uhr → etwas breiter. */
  idleMitText?: boolean;
  /** Ruhende Pille zeigt den laufenden Ticket-Timer (Uhr · Ticket · Stopp) → breiter, kein Hover-Peek. */
  idleTimer?: boolean;
  /** Sichtbarer Viewport — deckelt Breite/Höhe der schwebenden Pille. */
  viewport: { width: number; height: number };
  /** Layout-Höhe (window.innerHeight), tastaturunabhängig — entscheidet allein
   *  über Vollbild ja/nein. */
  layoutHoehe: number;
}

/** Auf dem Handy übernehmen die GEÖFFNETEN Zustände (Formular und Deck) den
 *  ganzen Bildschirm. Ein schwebendes Panel hat auf 390 px keinen Platz — die
 *  Formulare sind für 480–720 px entworfen. Ruhezustände (idle/live/toast)
 *  bleiben die kleine Pille, das ist ihr ganzer Sinn.
 *  `layoutHoehe` ist bewusst die Höhe des LAYOUT-Viewports (window.innerHeight):
 *  die sichtbare Höhe schrumpft auf iOS mit der Tastatur, und ein Tastendruck
 *  darf die Darstellung nicht von Panel auf Vollbild umschalten. */
export function istVollbild(mode: IslandMode, viewportWidth: number, layoutHoehe: number): boolean {
  return istKompakt(viewportWidth, layoutHoehe) && (mode === 'form' || mode === 'deck');
}

export function computeIslandSize(
  {
    mode, deckPage, peeking, controlsContentHeight, formWidth, formContentHeight, anrufHatAuswahl, anrufKontext, anrufUnbekannt, idleMitText, idleTimer,
    viewport, layoutHoehe,
  }: SizeArgs,
): { w: number; h: number; r: number } {
  const base = peeking ? SIZE_PEEK : SIZE[mode];
  let h = mode === 'deck' ? DECK_PAGE_HEIGHT[deckPage] : base.h;
  if (mode === 'deck' && deckPage === 'controls' && controlsContentHeight) {
    h = Math.max(h, controlsContentHeight + DECK_HEADER_HEIGHT);
  }
  if (mode === 'form' && formContentHeight) {
    h = Math.min(formContentHeight, SIZE.form.h);
  }
  if (mode === 'anruf') {
    if (anrufHatAuswahl) {
      // Auswahlliste bei mehrdeutiger Nummer: feste, größere Höhe — es gibt
      // noch keinen Treffer und damit auch keinen Zusammenhang zu zeigen.
      h = SIZE_ANRUF_AUSWAHL_H;
    } else if (anrufKontext?.hatInhalt || anrufKontext?.fehler) {
      // Zusammenhang (Objekt/Einheit + offene Vorgänge) und/oder eine Fehlerzeile:
      // wächst nur um so viel, wie tatsächlich Zeilen da sind. Tickets zählen mit
      // ANRUF_KONTEXT_TICKET_ZEILE_H (tap-44-Buttons, min. 44px), alle anderen
      // Zeilen (Objektzeile, "+ N weitere", Fehlerzeile) mit dem kleineren
      // ANRUF_KONTEXT_ZEILE_H. Ohne Zusammenhang bleibt es bei SIZE.anruf.h.
      const textZeilen =
        (anrufKontext.objektZeile ? 1 : 0) +
        (anrufKontext.weitereTickets > 0 ? 1 : 0) +
        (anrufKontext.fehler ? 1 : 0);
      h = SIZE.anruf.h + ANRUF_KONTEXT_TRENNER_H + textZeilen * ANRUF_KONTEXT_ZEILE_H
        + anrufKontext.tickets.length * ANRUF_KONTEXT_TICKET_ZEILE_H;
    } else if (anrufUnbekannt) {
      h = SIZE.anruf.h + ANRUF_UNBEKANNT_H;
    }
  }
  // Breite: form-Mode nutzt formWidth; deck-Mode kann pro Seite breiter sein
  // (sicher, da das Deck nur die aktive Seite rendert — kein Carousel mehr).
  const baseW =
    mode === 'form' && formWidth
      ? formWidth
      : mode === 'deck'
        ? (DECK_PAGE_WIDTH[deckPage] ?? base.w)
        : mode === 'idle' && idleTimer
          ? IDLE_TIMER_W
          : mode === 'idle' && !peeking && idleMitText
            ? IDLE_MIT_TEXT_W
            : base.w;
  if (istVollbild(mode, viewport.width, layoutHoehe)) {
    // Volle Breite, volle SICHTBARE Höhe, keine Rundung: Der Aufrufer übergibt
    // in `viewport.height` bereits die Höhe des sichtbaren Viewports — bei
    // eingeblendeter Tastatur also die Höhe darüber, wodurch die Fläche an der
    // Tastaturkante endet. Die 480–720px-Masken haben auf 390px sonst keinen
    // Platz. Position/Verschiebung: DynamicIsland.
    return { w: viewport.width, h: viewport.height, r: 0 };
  }

  // Aktivität auf dem Handy (< 768 px): nicht breiter als die Lücke zwischen Menü-Knopf
  // und Profilbild der mobilen Kopfzeile (je ~77 px Rand, auf 390 px also 236 = Ruhe-Pille).
  const w = (mode === 'live' || (mode === 'idle' && idleTimer)) && viewport.width < 768
    ? Math.min(baseW, Math.max(IDLE_MIT_TEXT_W, viewport.width - 154))
    : baseW;
  return {
    w: Math.min(w, viewport.width - 16),
    h: Math.min(h, viewport.height - 96),
    r: base.r,
  };
}
